import { useEffect, useRef, useState } from "react";
import { api, ApiError, request, type ChatStopStatus, type TurnProgress } from "../lib/api";
import { commandFeed } from "../lib/commandFeed";
import { Icon } from "../components/Icon";

/** One stop per reply, named after the reply it stops.
 *
 * Any tab that can see the turn asks about the same request, a lost HTTP reply
 * cannot mint a second one, and two tabs pressing the button stop the reply
 * once. The server enforces the other half: one id never belongs to two inputs.
 */
const stopCommandId = (turnId: string) =>
  `chat-stop-${turnId.replace(/[^A-Za-z0-9_-]/g, "-").slice(0, 80)}`;

/** The Owner's request to stop this reply, and the receipt for it.
 *
 * Both live on the server, in the conversation they belong to: asking
 * `/api/chat/<session>/stop` without an id is asking what this conversation
 * last requested, so a second tab -- or this one after a navigation -- shows
 * the same request and the same receipt. It used to be kept in `sessionStorage`
 * and went with the page, leaving the Owner no trace of his own request (D650).
 */
export function ChatStop({ sessionId, progress, latestInputAt, connected, hasBackgroundWork = false, onStatus }: {
  sessionId: string;
  progress: TurnProgress | null;
  latestInputAt: number | null;
  /** Declared because the conversation still passes it; deliberately unread. */
  version?: number;
  connected: boolean;
  hasBackgroundWork?: boolean;
  onStatus?: (status: ChatStopStatus | null) => void;
}) {
  const [status, setStatus] = useState<ChatStopStatus | null>(null);
  /** What this tab just sent, until a receipt answers for it.
   *
   * Not a second home for the record and never written to storage: it exists
   * for the one state the server cannot describe, a send whose HTTP reply was
   * lost. Without it a failed send left the page with no request to retry.
   */
  const [attempted, setAttempted] = useState<{ command_id: string; turn_id: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [posting, setPosting] = useState(false);
  const [checking, setChecking] = useState(false);
  const [tick, setTick] = useState(0);
  const busy = useRef(false);
  const alive = useRef(true);
  /** The server's record, when it is about the request this page is showing. */
  const receipt = status && (!attempted || status.turn_id === attempted.turn_id) ? status : null;
  const resolved = Boolean(receipt?.terminal || (receipt?.received && !receipt.accepted &&
    ["completed", "failed", "unknown"].includes(receipt.request_status || "")));
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);
  useEffect(() => { onStatus?.(status); }, [onStatus, status]);

  const adopt = (value: ChatStopStatus | null) => {
    if (!alive.current) return;
    setStatus(value); setError(null);
    // The conversation answered about another reply, so this tab's unanswered
    // send is not on the record: say so rather than keep describing it.
    setAttempted(previous =>
      previous && value && value.turn_id !== previous.turn_id ? null : previous);
  };
  // One read per tick: on arrival, after a send, and on every recheck below.
  useEffect(() => {
    if (!connected || posting) return;
    const controller = new AbortController();
    setChecking(true);
    request<ChatStopStatus | null>(`/api/chat/${encodeURIComponent(sessionId)}/stop`,
      { signal: controller.signal })
      .then(value => { if (!controller.signal.aborted) adopt(value); })
      .catch(failure => { if (!controller.signal.aborted) setError(ApiError.fromUnknown(failure).detail); })
      .finally(() => { if (!controller.signal.aborted) setChecking(false); });
    return () => controller.abort();
  }, [sessionId, connected, posting, tick]);
  // Read again when the record moves, and at no other time (CHARTER §7.194).
  // The receipt changes when the stop lands or the reply it stops ends, and
  // both are said by the server: a `command` frame about the stop's own
  // obligation or about the reply's, and the turn's progress ending. Not the
  // stream's `version` -- with that in the dependencies one request was read
  // 1884 times in 95 seconds (D651) -- and not a clock, which read a settled
  // record every two seconds until it was.
  useEffect(() => {
    if (!connected || posting || !receipt || resolved) return;
    return commandFeed.subscribe(frame => {
      if (frame.obligation_id === receipt.obligation_id || frame.command_id === receipt.command_id
        || (progress?.obligation_id && frame.obligation_id === progress.obligation_id)) setTick(value => value + 1);
    });
  }, [connected, posting, receipt?.command_id, receipt?.obligation_id, progress?.obligation_id, resolved]);
  const turn = `${progress?.turn_id ?? ""}:${progress?.state ?? ""}`;
  const seenTurn = useRef(turn);
  useEffect(() => {
    if (seenTurn.current === turn) return;
    seenTurn.current = turn;
    if (receipt && !resolved) setTick(value => value + 1);
  }, [turn, receipt, resolved]);

  const send = async (turnId: string) => {
    if (busy.current || !connected) return;
    busy.current = true;
    const command = stopCommandId(turnId);
    setAttempted({ command_id: command, turn_id: turnId });
    setPosting(true); setError(null);
    try {
      const value = await api.stopChat(sessionId, turnId, command);
      if (value.command_id !== command || value.session_id !== sessionId || value.turn_id !== turnId)
        throw new Error("停止回执与原请求不一致，尚未确认停止结果。");
      adopt(value);
    } catch (failure) {
      if (alive.current) setError(ApiError.fromUnknown(failure).detail);
    } finally {
      busy.current = false;
      if (alive.current) setPosting(false);
    }
  };
  const target = progress?.dispatch_mode === "turn" && progress.turn_id ? progress.turn_id : null;
  // The request this page is about: the conversation's record, or -- only while
  // a send of this tab's is unanswered -- what it just sent.
  const asked = receipt ?? attempted;
  const canStart = Boolean(target && (!asked || (resolved && asked.turn_id !== target)));
  const newerInput = receipt?.requested_at != null && latestInputAt != null && latestInputAt > receipt.requested_at;
  // Keep unresolved requests reviewable. A confirmed result stops describing
  // the current conversation once a later input has actually arrived.
  const showAttempt = Boolean(asked && (!resolved || (latestInputAt !== null && !newerInput)));
  // A settled receipt belongs beside the turn it stopped in the conversation.
  // The action row near the composer keeps only work that can still change.
  if (!canStart && (!showAttempt || receipt?.terminal)) return null;
  const previous = newerInput || Boolean(asked && target && target !== asked.turn_id);
  const subject = previous ? "上一条回复" : "当前回复";
  const stopped = receipt?.terminal && receipt.outcome === "stopped";
  const waiting = Boolean(receipt?.accepted && !receipt.terminal);
  const message = !showAttempt ? null : posting ? "正在发送停止请求…" : error ? (asked ? "停止结果尚未确认" : "尚未发送停止请求")
    : stopped ? null
    : receipt?.terminal ? `${subject}已结束，未收到停止终态`
    : receipt?.received && !receipt.accepted ? "停止请求未被接受"
    : waiting ? previous ? "正在停止先前回复，等待调用结束" : "正在停止，等待当前调用结束"
    : asked ? "尚未确认收到停止请求" : null;
  const reason = showAttempt ? receipt?.reason?.replace(/^[a-z_]+:\s*/, "") : null;
  return <section className="chat-stop" aria-label="停止当前回复">
    {canStart ? <button className="btn" type="button" disabled={!connected || posting}
      onClick={() => void send(target!)}>
      <Icon name="stop" size={16} />停止当前回复
    </button> : null}
    {message ? <p role="status">{message}</p> : null}
    {reason && !receipt?.accepted ? <p data-quoted="true">{reason}</p> : null}
    {showAttempt && receipt?.error ? <details className="micro"><summary>查看本次调用的错误记录</summary><p data-quoted="true">{receipt.error}</p></details> : null}
    {showAttempt && asked ? <>
      {hasBackgroundWork ? <p className="micro">独立的后台工作会继续进行。</p> : null}
      {error ? <p className="micro" role="alert">{error}</p> : null}
      {!resolved ? <div className="chat-stop-actions">
        <button className="btn" type="button" disabled={!connected || posting || checking}
          onClick={() => setTick(value => value + 1)}>{checking ? "正在核对…" : "查看停止结果"}</button>
        {!receipt?.received || error ? <button className="btn" type="button" disabled={!connected || posting}
          onClick={() => void send(asked.turn_id)}>重试同一停止请求</button> : null}
      </div> : null}
    </> : null}
  </section>;
}
